/*
 * Copyright (c) 2022. Nedaware SL
 */

import {HttpParams, HttpResponse} from '@angular/common/http';
import {ApiService} from './api.service';
import {Observable} from 'rxjs';
import {Paginated} from './collection-answer';
import {map} from 'rxjs/operators';
import {EntityInterface} from './entity-interface';
import {HttpUrlEncodingCodecCorrected} from './http-url-encoding-codec';
import {Resource} from "./resource";
import {FilterList} from "./filter-list";

/**
 * Clase que se puede extender para obtener servicios básicos para una entidad
 * que disponga de una EntityInterface asociada.
 * Este service provee: <ol>
 *     <li>Un método find que llamará al get en el endpoint de la entity (paginado y ordenado, opcionalmente).</li>
 *     <li>Un método remove que llamará al delete en el endpoint de colección de la entidades </li>
 *     <li>Un método update que llamará al update en el endpoint para la entidad</li>
 *     <li>Un método findById que llamará al get en el endpoint para la entidad</li>
 *     <li>Un método add que llamará al post en el endpoint de colección de la entidad</li>
 * </ol>
 */
export class EntityLoader<T = Resource, I extends EntityInterface<T> = EntityInterface<T>> {
    constructor(private _interface: I, private _urlservice: ApiService) {

    }

    /**
     * Find entities and keep requesting while the last page is not reached.
     * The returned observable will throw a new paginated for each page obtained.
     * @param filtros - Filters to use
     * @param sortParam - Param to sort by
     * @param sortDirection - Direction to sort
     * @param pageLimit - Limit of pages
     * @param pageIndex - Initial page index
     * @param pageSize - Page size to request
     */
    public findAndFollow(filtros?: FilterList, sortParam?: string, sortDirection?: 'asc' | 'desc',
                         pageLimit?: number, pageIndex?: number, pageSize?: number): Observable<Paginated<T>> {
        const params = this._filtersAndSortToParams(filtros, sortParam, sortDirection);
        return this._urlservice.getCollectionAndFollow(
            this._interface.endpoint,
            params, pageIndex, pageSize, pageLimit)
            .pipe(ApiService.sTakeBody())
            .pipe(ApiService.sPaginationConversion(this._interface.fromGetToModel.bind(this._interface)));
    }

    public find(filtros?: FilterList, sortParam?: string, sortDirection?: 'asc' | 'desc',
                pageIndex?: number, pageSize?: number):
        Observable<Paginated<T>> {
        const params = this._filtersAndSortToParams(filtros, sortParam, sortDirection);
        return this._urlservice.getCollection(
            this._interface.endpoint,
            params, pageIndex, pageSize)
            .pipe(ApiService.sTakeBody())
            .pipe(ApiService.sPaginationConversion(this._interface.fromGetToModel.bind(this._interface)));
    }

    private _filtersAndSortToParams(filtros?: FilterList, sortParam?: string, sortDirection?: 'asc' | 'desc'): HttpParams {
        return ApiService.sSetOrderParams(
            new HttpParams(
                filtros ?
                    {
                        fromObject: this._interface.filtersToParams(filtros),
                        encoder: new HttpUrlEncodingCodecCorrected()
                    } :
                    undefined),
            sortParam !== null ?
                this._interface.paramToSort(sortParam) :
                null,
            sortDirection);
    }

    public findById(...id): Observable<T> {
        return this._urlservice.get(this._interface.endpointForId(...id))
            .pipe(ApiService.sTakeBody())
            .pipe(map(this._interface.fromGetToModel.bind(this._interface)));
    }

    public remove(model: T): Observable<HttpResponse<any>> {
        console.log('Eliminar')
        return this._urlservice.del(this._interface.endpointFor(model));
    }

    public update(model: T): Observable<T> {

        return this._urlservice.put(this._interface.endpointFor(model), this._interface.forPut(model))
            .pipe(ApiService.sTakeBody())
            .pipe(map(this._interface.fromPutToModel.bind(this._interface)));
    }

    public add(model: T): Observable<T> {
        return this._urlservice.post(this._interface.endpoint, this._interface.forPost(model))
            .pipe(ApiService.sTakeBody())
            .pipe(map(this._interface.fromPostToModel.bind(this._interface)));
    }

    get entityInterface(): I {
        return this._interface;
    }
}
